import { useEffect, useState } from 'react';
import { ConsentManager, CLEVERTAP_CONSENT_CATEGORIES } from '@lib/consent';
import { datadogRumService } from '@lib/datadogRum';

interface DntStatus {
  hasRequestedDNT: boolean;
  isLoading: boolean;
}

export interface ConsentState {
  /** True once the initial CookieYes read has completed. */
  hasChecked: boolean;
  /** Advertisement category — gates the ad/marketing <Script> tags. */
  advertisement: boolean;
  /** Analytics category — gates PostHog capture/identify and Datadog RUM. */
  analytics: boolean;
  /** Category governing CleverTap (see CLEVERTAP_CONSENT_CATEGORIES). */
  clevertap: boolean;
  /** The single shared ConsentManager (exposed for setUserId, FFH-1172). */
  manager: ConsentManager;
}

const DENIED = {
  hasChecked: false,
  advertisement: false,
  analytics: false,
  clevertap: false
};

/**
 * Single source of truth for CookieYes consent. Owns ONE ConsentManager and
 * ONE `cookieyes_consent_update` listener, reads the relevant categories on
 * the initial check and on every change, and drives the imperative SDKs that
 * have no natural component home (Datadog RUM). PostHog and CleverTap apply the
 * returned booleans from their own effects (they need init/ready timing they
 * own). Under Do-Not-Track every category is forced denied — note that PostHog
 * feature-flag evaluation is deliberately NOT gated here (see @lib/posthog).
 */
export const useConsentOrchestrator = (dntStatus: DntStatus): ConsentState => {
  const [manager] = useState(() => new ConsentManager());
  const [state, setState] = useState<Omit<ConsentState, 'manager'>>(DENIED);

  useEffect(() => {
    if (dntStatus.isLoading) return;
    const dnt = dntStatus.hasRequestedDNT;

    // RUM is initialised eagerly (once DNT is known) so it is available as the
    // error sink; it starts 'not-granted' and sends nothing until the initial
    // check / listener upgrades it. init() is idempotent across re-runs.
    datadogRumService.init(false);

    const apply = async () => {
      // Under DNT force everything denied without touching CookieYes.
      const advertisement =
        !dnt && (await manager.hasConsent(['advertisement']));
      const analytics = !dnt && (await manager.hasConsent(['analytics']));
      const clevertap =
        !dnt && (await manager.hasConsent(CLEVERTAP_CONSENT_CATEGORIES));

      datadogRumService.setConsent(analytics);

      setState({ hasChecked: true, advertisement, analytics, clevertap });
    };

    // Preserve the ~500ms CookieYes settle used for the ad pixels so we don't
    // read a false-negative before the CMP has hydrated getCkyConsent().
    const timer = setTimeout(apply, 500);

    manager.startListening();
    manager.setConsentChangeCallback(() => {
      // grant-after-load and revoke both land here.
      apply();
    });

    return () => {
      clearTimeout(timer);
      manager.stopListening();
    };
  }, [manager, dntStatus.isLoading, dntStatus.hasRequestedDNT]);

  return { ...state, manager };
};
